import React from 'react';
import ReactDOM from 'react-dom/client';
import "./styles/base.css";
import App from './App';
import reportWebVitals from './reportWebVitals';

const preventZoomShortcuts = (event: WheelEvent | KeyboardEvent | TouchEvent | Event) => {
  if (event instanceof WheelEvent && event.ctrlKey) {
    event.preventDefault();
    return;
  }

  if (event instanceof KeyboardEvent && (event.ctrlKey || event.metaKey)) {
    const blockedKeys = ["+", "-", "=", "_", "Add", "Subtract", "0"];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
      return;
    }
  }

  if (event instanceof TouchEvent && event.touches.length > 1) {
    event.preventDefault();
  }
};

document.addEventListener("wheel", preventZoomShortcuts, { passive: false });
document.addEventListener("keydown", preventZoomShortcuts, { passive: false });
document.addEventListener("touchmove", preventZoomShortcuts, { passive: false });
document.addEventListener("gesturestart", preventZoomShortcuts as EventListener, { passive: false });
document.addEventListener("gesturechange", preventZoomShortcuts as EventListener, { passive: false });

const INLINE_ERROR_CLASS = "form-inline-error";

const isFieldControl = (
  element: EventTarget | null,
): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement => {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement;
};

const getInlineErrorMessage = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
  if (field.validity.valueMissing) {
    return "This field is required.";
  }

  if (field.validity.typeMismatch) {
    return field.validationMessage || "Please enter a valid value.";
  }

  if (field.validity.patternMismatch) {
    return field.validationMessage || "Please match the requested format.";
  }

  return field.validationMessage || "Please check this field.";
};

const findInlineErrorElement = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
  const nextSibling = field.nextElementSibling;
  if (nextSibling instanceof HTMLElement && nextSibling.classList.contains(INLINE_ERROR_CLASS)) {
    return nextSibling;
  }
  return null;
};

const clearInlineError = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
  const existingError = findInlineErrorElement(field);
  if (existingError) {
    existingError.remove();
  }

  field.removeAttribute("aria-invalid");
};

const showInlineError = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => {
  const message = getInlineErrorMessage(field);
  const existingError = findInlineErrorElement(field);

  field.setAttribute("aria-invalid", "true");

  if (existingError) {
    existingError.textContent = message;
    return;
  }

  const errorElement = document.createElement("small");
  errorElement.className = `${INLINE_ERROR_CLASS} form-error`;
  errorElement.textContent = message;
  field.insertAdjacentElement("afterend", errorElement);
};

document.addEventListener(
  "invalid",
  (event) => {
    if (!isFieldControl(event.target)) {
      return;
    }

    event.preventDefault();
    showInlineError(event.target);
  },
  true,
);

document.addEventListener(
  "input",
  (event) => {
    if (!isFieldControl(event.target)) {
      return;
    }

    if (event.target.validity.valid) {
      clearInlineError(event.target);
    } else if (event.target.getAttribute("aria-invalid") === "true") {
      showInlineError(event.target);
    }
  },
  true,
);

document.addEventListener(
  "change",
  (event) => {
    if (!isFieldControl(event.target)) {
      return;
    }

    if (event.target.validity.valid) {
      clearInlineError(event.target);
    } else if (event.target.getAttribute("aria-invalid") === "true") {
      showInlineError(event.target);
    }
  },
  true,
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
